import React, { useState, useEffect, useMemo } from 'react';
import useFetch from '../../../services/useFetch.js';
import GetData from '../../../services/GetData.js';
import PostDataRetData from '../../../services/PostDataRetData.js';
import {
  Autocomplete, Box, Button, CircularProgress, IconButton, Stack, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { tokens } from '../../../theme';

function extractIngredients(doc) {
  if (!doc) return [];
  // Product detail envelope: { data: { ingredients } }
  if (Array.isArray(doc?.data?.ingredients)) return doc.data.ingredients;
  if (Array.isArray(doc.ingredients)) return doc.ingredients;
  if (Array.isArray(doc.supplies)) return doc.supplies;
  return [];
}

function parseBottleOz(product, recipeLines = []) {
  const batchLine = (recipeLines || []).find((l) => l?.type === 'batch');
  if (batchLine) {
    const amt = Number(batchLine.units?.[0]?.amount ?? batchLine.amount);
    if (Number.isFinite(amt) && amt > 0) return amt;
  }
  // Any positive recipe amount in oz (legacy lines without type: batch)
  for (const line of recipeLines || []) {
    const measurement = String(line?.units?.[0]?.measurement ?? line?.measurement ?? '').toLowerCase();
    const amt = Number(line?.units?.[0]?.amount ?? line?.amount);
    if (measurement === 'oz' && Number.isFinite(amt) && amt > 0) return amt;
  }
  const text = `${product?.variant || ''} ${product?.itemName || ''}`;
  const m = text.match(/(\d+(?:\.\d+)?)\s*oz\b/i);
  return m ? Number(m[1]) : null;
}

function recipeForProduct(recipes, item) {
  if (!Array.isArray(recipes) || !item) return null;
  const itemId = Number(item.itemId);
  const posId = item.posItemId != null ? String(item.posItemId) : null;
  return recipes.find((r) => {
    if (r?.isDeleted) return false;
    if (Number.isFinite(itemId) && Number(r.posMenuItemId) === itemId) return true;
    if (posId && String(r.posItemId) === posId) return true;
    return false;
  }) || null;
}

function sizeLabel(item, bottleOz) {
  if (item?.variant && String(item.variant).trim() && item.variant !== 'None') {
    return item.variant;
  }
  if (bottleOz != null) return `${bottleOz} oz`;
  return item?.itemName || 'Size';
}

export default function AddSku({ close }) {
  const { data: menuItems } = useFetch('api/menuItems', false);
  const { data: customMenuData } = useFetch('api/custom-menu-item', false);
  const { data: recipes } = useFetch('api/menuItemsRecipes', false);
  const { data: batchesEnvelope } = useFetch('api/batches', false);
  const batches = batchesEnvelope?.data || [];

  const [allItems, setAllItems] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [sizeQtys, setSizeQtys] = useState({});
  const [sizeOz, setSizeOz] = useState({});
  const [loadingSizes, setLoadingSizes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!menuItems || !customMenuData) return;
    const merged = [...menuItems, ...customMenuData];
    setAllItems(merged);
  }, [customMenuData, menuItems]);

  const families = useMemo(() => {
    const byName = new Map();
    for (const item of allItems) {
      const name = (item.itemName || '').trim() || 'Untitled';
      if (!byName.has(name)) byName.set(name, []);
      byName.get(name).push(item);
    }
    return [...byName.entries()]
      .map(([itemName, variants]) => ({
        itemName,
        variants,
        label: itemName,
      }))
      .sort((a, b) => a.itemName.localeCompare(b.itemName));
  }, [allItems]);

  // Resolve bottle oz per size variant when a product family is selected
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedFamily?.variants?.length) {
        setSizeOz({});
        setSizeQtys({});
        return;
      }
      setLoadingSizes(true);
      setError('');
      const ozMap = {};
      const qtyMap = {};

      for (const item of selectedFamily.variants) {
        const key = String(item.itemId);
        let oz = parseBottleOz(item, extractIngredients(item));

        if (oz == null) {
          const linked = recipeForProduct(recipes, item);
          oz = parseBottleOz(item, extractIngredients(linked));
        }

        if (oz == null) {
          const ids = [...new Set([
            item.posItemId,
            item.itemId,
          ].filter((v) => v != null && v !== ''))];
          for (const id of ids) {
            // Prefer product detail (reads Recipe collection + batch line amount)
            const { getData: detail } = await GetData(
              `api/products/${encodeURIComponent(id)}/detail`,
              null, null, null, null,
            );
            oz = parseBottleOz(item, extractIngredients(detail));
            if (oz != null) break;

            const { getData: recipeInfo } = await GetData(
              `api/menuItemsRecipe/${encodeURIComponent(id)}`,
              null, null, null, null,
            );
            oz = parseBottleOz(item, extractIngredients(recipeInfo));
            if (oz != null) break;
          }
        }

        if (oz != null) {
          ozMap[key] = oz;
          qtyMap[key] = '';
        }
      }

      if (!cancelled) {
        setSizeOz(ozMap);
        setSizeQtys(qtyMap);
        setLoadingSizes(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedFamily, recipes]);

  const sizeRows = useMemo(() => {
    if (!selectedFamily?.variants) return [];
    return selectedFamily.variants
      .map((item) => {
        const key = String(item.itemId);
        const bottleOz = sizeOz[key];
        if (bottleOz == null) return null;
        return {
          key,
          item,
          bottleOz,
          type: item.type ? 'custom' : 'pos',
          label: sizeLabel(item, bottleOz),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.bottleOz - b.bottleOz || a.label.localeCompare(b.label));
  }, [selectedFamily, sizeOz]);

  const lineQtys = useMemo(() => {
    return sizeRows.map((row) => {
      const n = Number(sizeQtys[row.key]);
      const quantity = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
      return { ...row, quantity };
    }).filter((r) => r.quantity > 0);
  }, [sizeRows, sizeQtys]);

  const totalOzNeeded = useMemo(
    () => lineQtys.reduce((sum, r) => sum + r.bottleOz * r.quantity, 0),
    [lineQtys],
  );

  const totalBottles = useMemo(
    () => lineQtys.reduce((sum, r) => sum + r.quantity, 0),
    [lineQtys],
  );

  const remainingOz = Number(selectedBatch?.remainingOz) || 0;
  const ozOk = !selectedBatch || totalOzNeeded <= remainingOz;

  const batchOptions = useMemo(() => {
    return (batches || []).map((b) => ({
      ...b,
      label: `${b.batchId} · ${b.remainingOz ?? 0} oz left`,
    }));
  }, [batches]);

  const setQty = (key, value) => {
    setSizeQtys((prev) => ({ ...prev, [key]: value }));
  };

  const addSkuSubmit = async () => {
    if (!selectedBatch || lineQtys.length === 0) return;
    if (!ozOk) {
      setError(`Need ${totalOzNeeded} oz but batch only has ${remainingOz} oz remaining`);
      return;
    }
    setSubmitting(true);
    setError('');
    const { data, error: err } = await PostDataRetData(
      {
        batchId: selectedBatch.batchId,
        lines: lineQtys.map((r) => ({
          parentItem: r.item.itemId,
          type: r.type,
          quantity: r.quantity,
        })),
      },
      'api/skus',
    );
    setSubmitting(false);

    if (err || data?.error) {
      setError(
        data?.error?.message
        || err?.response?.data?.error?.message
        || 'Could not create SKUs',
      );
      return;
    }
    close(true);
  };

  const canSubmit = Boolean(selectedFamily)
    && Boolean(selectedBatch)
    && lineQtys.length > 0
    && ozOk
    && !submitting
    && !loadingSizes;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2.5, pt: 2.25, pb: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: tokens.accent,
                fontWeight: 700,
                mb: 0.75,
              }}
            >
              New SKUs
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Fraunces', 'Figtree', serif",
                fontWeight: 650,
                fontSize: 24,
                color: tokens.ink,
                letterSpacing: '-0.02em',
              }}
            >
              Bottle from batch
            </Typography>
            <Typography sx={{ mt: 0.5, color: tokens.inkMuted, fontSize: 13 }}>
              Choose a product and batch, then enter qty for each bottle size.
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={() => close(false)} size="small" sx={{ color: tokens.inkMuted }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ px: 2.5, pb: 2, borderBottom: `1px solid ${tokens.border}` }}>
        <Stack spacing={1.5}>
          <Autocomplete
            options={families}
            loading={families.length === 0}
            size="small"
            value={selectedFamily}
            onChange={(_, val) => {
              setSelectedFamily(val);
              setSelectedBatch(null);
              setError('');
            }}
            getOptionLabel={(o) => o?.label || o?.itemName || ''}
            isOptionEqualToValue={(a, b) => a?.itemName === b?.itemName}
            renderInput={(params) => (
              <TextField {...params} label="Product" placeholder="Search products" />
            )}
          />
          <Autocomplete
            options={batchOptions}
            size="small"
            value={selectedBatch}
            onChange={(_, val) => setSelectedBatch(val)}
            getOptionLabel={(o) => o?.label || o?.batchId || ''}
            isOptionEqualToValue={(a, b) => a?.batchId === b?.batchId}
            disabled={!selectedFamily}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Production batch"
                placeholder={selectedFamily ? 'Select batch' : 'Select a product first'}
              />
            )}
          />
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
        {!selectedFamily && (
          <Typography sx={{ color: tokens.inkMuted, fontSize: 14, py: 2 }}>
            Select a product to see bottle size options.
          </Typography>
        )}
        {selectedFamily && loadingSizes && (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {selectedFamily && !loadingSizes && sizeRows.length === 0 && (
          <Typography sx={{ color: tokens.warn, fontSize: 13, py: 2 }}>
            No bottle sizes with a readable oz amount were found for this product.
            Open the product, set it to Made, enter Oz per bottle on the production batch
            line, and save the recipe. Variant names like &quot;4oz&quot; also work.
          </Typography>
        )}
        {selectedFamily && !loadingSizes && sizeRows.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: tokens.inkMuted,
                mb: 1.25,
              }}
            >
              Bottle sizes
            </Typography>
            <Box
              sx={{
                border: `1px solid ${tokens.border}`,
                borderRadius: 1.5,
                overflow: 'hidden',
              }}
            >
              {sizeRows.map((row, indx) => (
                <Box
                  key={row.key}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 110px',
                    gap: 1.5,
                    alignItems: 'center',
                    px: 1.5,
                    py: 1.25,
                    borderBottom: indx === sizeRows.length - 1 ? 'none' : `1px solid ${tokens.border}`,
                    backgroundColor: tokens.bg,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 650, color: tokens.ink }}>
                      {row.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mt: 0.15 }}>
                      {row.bottleOz} oz each
                    </Typography>
                  </Box>
                  <TextField
                    size="small"
                    type="number"
                    label="Qty"
                    value={sizeQtys[row.key] ?? ''}
                    onChange={(e) => setQty(row.key, e.target.value)}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Box>
              ))}
            </Box>

            {selectedBatch && (
              <Box
                sx={{
                  mt: 1.75,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 1.5,
                  backgroundColor: tokens.bg,
                  border: `1px solid ${ozOk ? tokens.border : tokens.danger}`,
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 650, color: tokens.ink }}>
                  {totalBottles > 0
                    ? `${totalBottles} bottle${totalBottles === 1 ? '' : 's'} · ${totalOzNeeded} oz`
                    : 'Enter qty for one or more sizes'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: ozOk ? tokens.inkMuted : tokens.danger,
                    mt: 0.25,
                  }}
                >
                  Batch {selectedBatch.batchId} has {remainingOz} oz remaining
                  {!ozOk ? ` (short ${totalOzNeeded - remainingOz} oz)` : ''}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {error && (
          <Typography sx={{ color: tokens.danger, fontSize: 13, mt: 1.5 }}>{error}</Typography>
        )}
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: `1px solid ${tokens.border}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <Button variant="outlined" onClick={() => close(false)} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={addSkuSubmit} disabled={!canSubmit}>
          {submitting
            ? <CircularProgress size={18} sx={{ color: '#fff' }} />
            : (totalBottles > 1 ? `Create ${totalBottles} SKUs` : 'Create SKU')}
        </Button>
      </Box>
    </Box>
  );
}
